<template>
  <v-menu
    v-model="isOpen"
    offset-y
    :close-on-content-click="false"
    max-width="420"
    min-width="360"
    :z-index="9999"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn class="mx-1" icon v-bind="attrs" @click.stop="toggle">
        <v-badge
          :content="unreadCount"
          :value="unreadCount"
          color="red"
          overlap
        >
          <v-icon
            size="20"
            :color="isScrolled ? 'blue' : '#fafafa'"
            class="notification-bell"
            >mdi-bell</v-icon
          >
        </v-badge>
      </v-btn>
    </template>

    <v-card class="notifications-panel" elevation="8">
      <!-- Header -->
      <div class="panel-header">
        <h3 class="panel-title">{{ $t("notifications.title") }}</h3>
        <v-btn
          icon
          small
          @click="showFilters = !showFilters"
          class="filter-toggle-btn pa-2"
        >
          <v-icon>mdi-filter-variant</v-icon>
        </v-btn>
      </div>

      <!-- Filter Panel -->
      <div class="filter-panel" v-if="showFilters">
        <v-row>
          <v-col cols="6" class="pa-1">
            <v-select
              dense
              hide-details
              :items="typeItems"
              v-model="filters.notification_type"
              :label="$t('notifications.type')"
              clearable
              class="styled-input"
              filled
              color="#008000"
            />
          </v-col>
          <v-col cols="6" class="pa-1">
            <v-select
              dense
              hide-details
              class="styled-input"
              filled
              :items="priorityItems"
              v-model="filters.priority"
              :label="$t('notifications.priority')"
              clearable
              color="#008000"
            />
          </v-col>
          <v-col cols="6" class="pa-1">
            <v-select
              dense
              hide-details
              class="styled-input"
              filled
              :items="readItems"
              v-model="filters.is_read"
              :label="$t('notifications.status')"
              clearable
              color="#008000"
            />
          </v-col>
        </v-row>
      </div>

      <!-- Filter Chips -->
      <div class="filter-section" v-if="hasActiveFilters">
        <v-chip
          v-if="filters.is_read !== null"
          small
          close
          @click:close="filters.is_read = null"
          class="filter-chip"
          color="#008000"
        >
          {{
            filters.is_read
              ? $t("notifications.read")
              : $t("notifications.unread")
          }}
        </v-chip>
        <v-chip
          v-if="filters.notification_type"
          small
          close
          @click:close="filters.notification_type = null"
          class="filter-chip"
          color="#008000"
        >
          {{ filters.notification_type }}
        </v-chip>
        <v-chip
          v-if="filters.priority"
          small
          close
          @click:close="filters.priority = null"
          class="filter-chip"
          color="#008000"
        >
          <v-icon left small>mdi-magnify</v-icon>
          {{ filters.priority }}
        </v-chip>
      </div>

      <!-- Notification List -->
      <div class="notifications-list">
        <v-skeleton-loader
          v-if="loading && !items.length"
          type="list-item-two-line@4"
          class="pa-3"
        />

        <template v-else>
          <div
            v-for="n in items"
            :key="n.id"
            class="notification-card"
            :class="{ unread: !n.is_read }"
          >
            <div class="notification-icon">
              <div
                class="icon-container"
                :class="getIconClass(n.notification_type)"
              >
                <span v-if="n.notification_icon">{{
                  n.notification_icon
                }}</span>
                <v-icon v-else>mdi-bell</v-icon>
              </div>
            </div>

            <div class="notification-content">
              <div class="notification-title">{{ n.title }}</div>
              <div class="notification-message">{{ n.message }}</div>
            </div>

            <div class="notification-actions">
              <v-chip
                small
                :color="getPriorityColor(n.priority)"
                text-color="white"
                class="priority-chip"
              >
                {{ n.priority }}
              </v-chip>
            </div>
          </div>

          <div v-if="!items.length && !loading" class="empty-state">
            <v-icon large color="grey lighten-1">mdi-bell-off</v-icon>
            <p>{{ $t("notifications.noNotifications") }}</p>
          </div>
        </template>
      </div>

      <!-- Pagination -->
      <div class="pagination-section">
        <span class="pagination-text">{{ getPaginationText() }}</span>
        <div class="pagination-controls">
          <v-btn
            icon
            small
            :disabled="getCurrentPage() <= 1"
            @click="goPrev"
            class="pagination-btn"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <!-- Page Numbers -->
          <div class="page-numbers">
            <v-btn
              v-for="page in getVisiblePages()"
              :key="page"
              x-small
              elevation="0"
              :color="page === getCurrentPage() ? 'primary' : 'grey lighten-3'"
              :text-color="
                page === getCurrentPage() ? 'white' : 'grey darken-1'
              "
              @click="goToPage(page)"
              class="page-btn mx-1"
            >
              {{ page }}
            </v-btn>
          </div>

          <v-btn
            icon
            small
            :disabled="getCurrentPage() >= getTotalPages()"
            @click="goNext"
            class="pagination-btn"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import GlobalMixin from "../../GlobalMixin"
import { mapActions, mapGetters } from "vuex"

export default {
  name: "NotificationsPanel",
  mixins: [GlobalMixin],
  props: {
    isScrolled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showFilters: false,
      isPaginationChange: false, // Flag to prevent watcher during pagination
      filters: {
        is_read: null,
        notification_type: null,
        priority: null,
        page: 1,
        limit: 20,
      },
      typeItems: [
        "homework_assigned",
        "homework_graded",
        "feedback_provided",
        "due_reminder",
      ],
      priorityItems: ["urgent", "high", "medium", "low"],
    }
  },
  computed: {
    ...mapGetters("notifications", {
      items: "notificationsList",
      count: "notificationsCount",
      next: "notificationsNext",
      previous: "notificationsPrevious",
      stats: "notificationsStats",
      loading: "notificationsLoading",
      isOpen: "notificationsShowPanel",
      unreadCount: "notificationsUnreadCount",
    }),
    hasActiveFilters() {
      return (
        this.filters.is_read !== null ||
        this.filters.notification_type !== null ||
        this.filters.priority !== null
      )
    },
    readItems() {
      return [
        { text: this.$t("notifications.unread"), value: false },
        { text: this.$t("notifications.read"), value: true },
      ]
    },
  },
  mounted() {
    // Ensure panel starts closed
    this.togglePanel(false)
  },
  watch: {
    filters: {
      handler() {
        // Skip watcher during pagination to prevent double API calls
        if (this.isPaginationChange) {
          this.isPaginationChange = false
          return
        }
        this.load(true) // Include stats when filters change
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions("notifications", [
      "fetchList",
      "fetchStats",
      "fetchPreferences",
      "markRead",
      "archive",
      "togglePanel",
    ]),
    async load(includeStats = false) {
      const params = { ...this.filters }
      if (params.is_read === null) delete params.is_read
      // Ensure limit is always 20 for pagination
      params.limit = 20
      // Ensure page parameter is included
      params.page = this.filters.page || 1
      try {
        await this.fetchList({ params, headers: this.headers })
        // Only fetch stats when needed (filter changes or initial load)
        if (includeStats) {
          await this.fetchStats({ headers: this.headers })
        }
      } catch (err) {
        this.formatErrors(err, "notify")
      }
    },
    refresh() {
      this.load(true) // Include stats on refresh
    },
    toggle() {
      this.togglePanel()
      if (this.isOpen) this.load(true) // Include stats when opening panel
    },
    goPrev() {
      if (!this.previous) return
      this.isPaginationChange = true
      this.filters.page = Math.max(1, (this.filters.page || 1) - 1)
      this.load()
    },
    goNext() {
      if (this.getCurrentPage() >= this.getTotalPages()) return
      this.isPaginationChange = true
      this.filters.page = this.getCurrentPage() + 1
      this.load()
    },
    goToPage(page) {
      if (page === this.getCurrentPage()) return
      this.isPaginationChange = true
      this.filters.page = page
      this.load()
    },
    getVisiblePages() {
      const current = this.getCurrentPage()
      const total = this.getTotalPages()
      const pages = []

      // Show up to 5 pages around current page
      const start = Math.max(1, current - 2)
      const end = Math.min(total, current + 2)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    },
    formatDate(iso) {
      if (!iso) return ""
      try {
        const d = new Date(iso)
        return d.toLocaleString()
      } catch (e) {
        return iso
      }
    },
    getPriorityColor(priority) {
      const colors = {
        urgent: "red",
        high: "orange",
        medium: "blue",
        low: "grey",
      }
      return colors[priority] || "grey"
    },
    getIconClass(type) {
      const classes = {
        homework_assigned: "icon-green",
        homework_graded: "icon-blue",
        feedback_provided: "icon-purple",
        due_reminder: "icon-orange",
      }
      return classes[type] || "icon-default"
    },
    getPaginationText() {
      const start = (this.filters.page - 1) * this.filters.limit + 1
      const end = Math.min(start + this.items.length - 1, this.count)
      return `${start}-${end} of ${this.count}`
    },
    getCurrentPage() {
      return this.filters.page || 1
    },
    getTotalPages() {
      return Math.ceil(this.count / this.filters.limit)
    },
    async markRead(n) {
      try {
        await this.$store.dispatch("notifications/markRead", {
          ids: n.id,
          headers: this.headers,
        })
      } catch (err) {
        this.formatErrors(err, "notify")
      }
    },
    async archive(n) {
      try {
        await this.$store.dispatch("notifications/archive", {
          ids: n.id,
          headers: this.headers,
          params: { ...this.filters },
        })
      } catch (err) {
        this.formatErrors(err, "notify")
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.notifications-panel {
  border-radius: 12px;
  overflow: hidden;
  background: white;
}

.panel-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .panel-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }

  .filter-toggle-btn {
    color: #666;
    &:hover {
      background: #f0f0f0;
    }
  }
}

.filter-panel {
  padding: 16px 20px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
  background: #646bd7;
}

.filter-section {
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .filter-chip {
    color: white;
    font-size: 12px;

    &:hover {
      background: #e0e0e0;
    }
  }
}

.notifications-list {
  max-height: 500px;
  overflow-y: auto;
  padding: 8px 0;
}

.notification-card {
  display: flex;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: #fafafa;
  }

  &.unread {
    background-color: #f0f2fb;
    border-left: 6px solid #2196f3;
  }

  &:last-child {
    border-bottom: none;
  }
}

.notification-icon {
  margin-right: 16px;
  flex-shrink: 0;

  .icon-container {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;

    &.icon-green {
      background: #e8f5e8;
      color: #4caf50;
    }

    &.icon-blue {
      background: #e3f2fd;
      color: #2196f3;
    }

    &.icon-purple {
      background: #f3e5f5;
      color: #9c27b0;
    }

    &.icon-orange {
      background: #fff3e0;
      color: #ff9800;
    }

    &.icon-default {
      background: #f5f5f5;
      color: #666;
    }
  }
}

.notification-content {
  flex: 1;
  min-width: 0;

  .notification-title {
    font-weight: 600;
    font-size: 14px;
    color: #333;
    margin-bottom: 4px;
    line-height: 1.3;
  }

  .notification-message {
    font-size: 13px;
    color: #666;
    line-height: 1.4;
    margin-bottom: 8px;
  }
}

.notification-actions {
  margin-left: 12px;
  flex-shrink: 0;

  .priority-chip {
    font-size: 11px;
    font-weight: 500;
    text-transform: capitalize;
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;

  p {
    margin: 12px 0 0 0;
    font-size: 14px;
  }
}

.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;

  .pagination-text {
    font-size: 13px;
    color: #666;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    .pagination-btn {
      color: #666;

      &:disabled {
        color: #ccc;
      }

      &:not(:disabled):hover {
        background: #e0e0e0;
      }
    }

    .page-numbers {
      display: flex;
      align-items: center;
      gap: 4px;

      .page-btn {
        min-width: 32px;
        max-width: 32px;
        height: 32px;
        font-size: 12px;
        font-weight: 500;
        border-radius: 4px;
        transition: all 0.2s ease;
        border-radius: 50%;
        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      }
    }
  }
}

// Scrollbar styling
.notifications-list::-webkit-scrollbar {
  width: 6px;
}

.notifications-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.notifications-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.notifications-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// Notification bell styling
.notification-bell {
  transform: rotate(45deg);
  transition: all 0.3s ease;

  &:hover {
    transform: rotate(-45deg) scale(1.1);
  }
}
</style>
